import { ReactNode, useCallback, useMemo } from "react";
import ApiContext, { ApiContextValue } from "~/contexts/ApiContext";
import useUserAuthentication from "~/hooks/contexts/useUserAuthentication";
import { restAPIBaseURL } from "~/lib/base";

export type ApiProviderProps = {
  children: ReactNode;
};

export default function ApiProvider({ children }: ApiProviderProps) {
  const { userAccessToken } = useUserAuthentication();

  const apiFetch = useCallback(async (path: string, init?: RequestInit) => {
    const headers: HeadersInit = {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...userAccessToken === undefined || userAccessToken === null ? {} : { authorization: `bearer ${userAccessToken.token}` },
      ...init?.headers,
    };

    return fetch(`${restAPIBaseURL}${path}`, { ...init, headers });
  }, [userAccessToken]);

  const value = useMemo<ApiContextValue>(() => ({
    delete(endpoint: string, payload?: object, config?: Omit<RequestInit, "body" | "method">) {
      return apiFetch(endpoint, { ...config, body: payload === undefined ? undefined : JSON.stringify(payload), method: "DELETE" });
    },
    get: apiFetch,
    patch(endpoint: string, payload?: object, config?: Omit<RequestInit, "body" | "method">) {
      return apiFetch(endpoint, { ...config, body: payload === undefined ? undefined : JSON.stringify(payload), method: "PATCH" });
    },
    post(endpoint: string, payload?: object, config?: Omit<RequestInit, "body" | "method">) {
      return apiFetch(endpoint, { ...config, body: payload === undefined ? undefined : JSON.stringify(payload), method: "POST" });
    },
  }), [apiFetch]);

  return (
    <ApiContext.Provider value={value}>
      {children}
    </ApiContext.Provider>
  );
}
